import React, { useEffect, useRef, useState } from "react";
import { KUIKA_BUTTON_SUCCESS_HOLD_MS, type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import type { KuikaDropdownMenuItemType } from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSplitButton from "../../../../ui-kit/kuika-split-button/kuika-split-button";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { VariantGrid } from "../_layout/variant-grid";

type SplitType = "primary" | "secondary";
type SplitSize = "sm" | "md" | "lg";

const SIZES: SplitSize[] = ["sm", "md", "lg"];
const TYPES: SplitType[] = ["primary", "secondary"];

const OPTIONS: KuikaDropdownMenuItemType[] = [
  { key: "draft", label: "Save as draft" },
  { key: "close", label: "Save and close" }
];

// A pretend save, long enough to watch the loading state.
const DEMO_SAVE_MS: number = 1200;

/** A split save that runs idle → loading → success → idle. */
const SplitStatusDemo = (): JSX.Element => {
  const [status, setStatus] = useState<KuikaButtonStatus>("idle");
  const timerRef: React.MutableRefObject<number | null> = useRef<number | null>(null);

  useEffect(
    (): (() => void) => (): void => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    },
    []
  );

  const handleSave = (): void => {
    setStatus("loading");
    timerRef.current = window.setTimeout((): void => {
      setStatus("success");
      timerRef.current = window.setTimeout((): void => setStatus("idle"), KUIKA_BUTTON_SUCCESS_HOLD_MS);
    }, DEMO_SAVE_MS);
  };

  return (
    <KuikaSplitButton options={OPTIONS} status={status} onClick={handleSave} aria-label="Save actions">
      Save
    </KuikaSplitButton>
  );
};

/** A split save whose menu carries a setting that goes with the alternatives. */
const SplitFooterDemo = (): JSX.Element => {
  const [isAutoFixOn, setIsAutoFixOn] = useState<boolean>(false);

  return (
    <KuikaSplitButton
      options={OPTIONS}
      aria-label="Save actions"
      menuFooter={
        <KuikaSwitch
          label="Auto-fix"
          labelPosition="left"
          checked={isAutoFixOn}
          onChange={(checked: boolean): void => setIsAutoFixOn(checked)}
        />
      }
    >
      Save
    </KuikaSplitButton>
  );
};

export const SplitButtonStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Split button"
      componentName="KuikaSplitButton"
      sourcePath="ui-kit/kuika-split-button"
      description="One action with its alternatives: a main button joined to a chevron that opens a menu. The pair reads and presses as one control."
    >
      <VariantGrid
        label="type · size"
        caption="square inner corners, one shared hairline — press either half and the pair gives"
        rows={TYPES}
        cols={SIZES}
        render={(type: string, size: string): React.ReactNode => (
          <KuikaSplitButton options={OPTIONS} type={type as SplitType} size={size as SplitSize}>
            Save
          </KuikaSplitButton>
        )}
      />
      <SpecRow label="status" caption="the main half carries the status; the menu stays shut while it is loading">
        <SplitStatusDemo />
      </SpecRow>
      <SpecRow label="menuFooter" caption="a setting under the alternatives — toggling it leaves the menu open">
        <SplitFooterDemo />
      </SpecRow>
      <SpecRow label="menuDisabled · disabled" caption="turn off only the alternatives, or both halves">
        <KuikaSplitButton options={OPTIONS} menuDisabled>
          Save
        </KuikaSplitButton>
        <KuikaSplitButton options={OPTIONS} disabled>
          Save
        </KuikaSplitButton>
      </SpecRow>
    </ShowcaseFrame>
  );
};
